import { Tooltip } from '@alphonsocode/kallos';
import React from 'react';
import { MdInfoOutline } from 'react-icons/md';
import * as S from '../style';
import { StreamMetadataDetails } from '../types';

interface StreamMetadataFieldsProps {
  details: StreamMetadataDetails | null;
  durationLabel?: string;
  error: string;
  isLoading?: boolean;
  isOptional?: boolean;
  onDurationChange: (value: string) => void;
  onUrlBlur: () => void;
  onUrlChange: (value: string) => void;
  title: string;
  urlLabel?: string;
  urlValue: string;
}

const formatSecondsAsDuration = (value: number) => {
  const totalSeconds = Math.max(0, Math.floor(value));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return [hours, minutes, seconds]
    .map(part => String(part).padStart(2, '0'))
    .join(':');
};

const parseDurationToSeconds = (value: string) => {
  const [hours, minutes, seconds] = value.split(':').map(Number);

  return hours * 3600 + minutes * 60 + seconds;
};

const normalizeDuration = (value?: string | number) => {
  const stringValue = String(value || '');

  if (stringValue && !stringValue.includes(':')) {
    const seconds = Number(stringValue);
    if (Number.isFinite(seconds)) return formatSecondsAsDuration(seconds);
  }

  const digits = stringValue.replace(/\D/g, '').slice(0, 6).padEnd(6, '0');
  return `${digits.slice(0, 2)}:${digits.slice(2, 4)}:${digits.slice(4, 6)}`;
};

const getTotalDurationSeconds = (
  streamMetadata: StreamMetadataDetails | null,
) => {
  const seconds = Number(streamMetadata?.totalDuration);

  return Number.isFinite(seconds) ? seconds : null;
};

const durationSlots = [0, 1, 3, 4, 6, 7];

const getDurationSlot = (cursorPosition: number, direction: 1 | -1 = 1) => {
  const slots = direction === 1 ? durationSlots : [...durationSlots].reverse();

  return (
    slots.find(slot =>
      direction === 1 ? slot >= cursorPosition : slot < cursorPosition,
    ) ?? (direction === 1 ? durationSlots[durationSlots.length - 1] : 0)
  );
};

const moveCursor = (input: HTMLInputElement, position: number) => {
  const nextPosition = durationSlots.includes(position)
    ? position
    : position + 1;
  requestAnimationFrame(() =>
    input.setSelectionRange(nextPosition, nextPosition),
  );
};

const replaceDurationDigit = (
  value: string,
  digit: string,
  cursorPosition: number,
) => {
  const chars = normalizeDuration(value).split('');
  const slot = getDurationSlot(cursorPosition);

  chars[slot] = digit;

  return {
    nextValue: chars.join(''),
    nextCursorPosition: slot === 1 || slot === 4 ? slot + 2 : slot + 1,
  };
};

const shouldResetVodDuration = (
  value: string,
  streamMetadata: StreamMetadataDetails | null,
) => {
  const isVod = streamMetadata?.streamType?.toLowerCase() === 'vod';
  const totalDurationSeconds = getTotalDurationSeconds(streamMetadata);

  return (
    isVod &&
    totalDurationSeconds !== null &&
    parseDurationToSeconds(value) > totalDurationSeconds
  );
};

export const StreamMetadataFields: React.FC<StreamMetadataFieldsProps> = ({
  details,
  durationLabel = 'Duration',
  error,
  isLoading = false,
  isOptional = false,
  onDurationChange,
  onUrlBlur,
  onUrlChange,
  title,
  urlLabel = 'Stream URL',
  urlValue,
}) => {
  const dimensions =
    details?.width && details?.height
      ? `${details.width} x ${details.height}`
      : '';
  const isVod = details?.streamType?.toLowerCase() === 'vod';
  const defaultDurationKey = details?.url || urlValue || title;
  const defaultDurations = React.useRef<Record<string, string>>({});

  if (details && !defaultDurations.current[defaultDurationKey]) {
    defaultDurations.current[defaultDurationKey] = normalizeDuration(
      details.defaultDuration,
    );
  }

  return (
    <S.FormSection>
      <S.AssetLabel>{title}</S.AssetLabel>
      <S.SubText>
        {isOptional
          ? 'Provide an HLS stream URL if you want to use a stream instead of a video asset.'
          : 'Provide an HLS stream URL. Metadata is fetched automatically when the field loses focus.'}
      </S.SubText>

      <S.FormRow>
        <S.FormGroup>
          <S.Label>{urlLabel}</S.Label>
          <S.Input
            value={urlValue}
            onChange={e => onUrlChange(e.target.value)}
            onBlur={onUrlBlur}
            placeholder="https://.../index.m3u8"
          />
        </S.FormGroup>
      </S.FormRow>

      {(isLoading || error || details) && (
        <>
          {error ? (
            <S.FormRow style={{ marginTop: '-8px' }}>
              <S.FormGroup>
                <span
                  style={{
                    color: 'red',
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  {error}
                </span>
              </S.FormGroup>
            </S.FormRow>
          ) : null}

          {isLoading ? (
            <S.FormRow style={{ marginTop: '-8px' }}>
              <S.FormGroup>
                <span style={{ color: '#555', fontSize: 12 }}>
                  Fetching stream metadata...
                </span>
              </S.FormGroup>
            </S.FormRow>
          ) : null}

          {details ? (
            <S.FormRow>
              <S.FormGroup>
                <S.Label
                  style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                >
                  {durationLabel}
                  {isVod ? (
                    <Tooltip
                      overlay="Duration cannot exceed the length of the video."
                      openDelay={0}
                      closeDelay={0}
                      arrow={false}
                      style={{
                        maxWidth: '220px',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                      }}
                    >
                      <span
                        aria-label="Duration info"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          color: '#137f95',
                          cursor: 'help',
                        }}
                      >
                        <MdInfoOutline size={15} />
                      </span>
                    </Tooltip>
                  ) : null}
                </S.Label>
                <S.Input
                  value={normalizeDuration(details.defaultDuration)}
                  onBlur={e => {
                    const value = normalizeDuration(e.target.value);
                    const totalDurationSeconds =
                      getTotalDurationSeconds(details) || 0;
                    if (shouldResetVodDuration(value, details)) {
                      onDurationChange(
                        defaultDurations.current[defaultDurationKey] ||
                          formatSecondsAsDuration(totalDurationSeconds),
                      );
                    }
                  }}
                  onChange={e =>
                    onDurationChange(normalizeDuration(e.target.value))
                  }
                  onKeyDown={e => {
                    if (!/^\d$/.test(e.key)) return;

                    e.preventDefault();

                    const input = e.currentTarget;
                    const { nextValue, nextCursorPosition } =
                      replaceDurationDigit(
                        input.value,
                        e.key,
                        input.selectionStart || 0,
                      );

                    onDurationChange(nextValue);
                    moveCursor(input, nextCursorPosition);
                  }}
                  placeholder="00:00:00"
                  inputMode="numeric"
                  maxLength={8}
                />
              </S.FormGroup>
              <S.FormGroup>
                <S.Label>Stream Type</S.Label>
                <S.Input value={details.streamType || ''} readOnly />
              </S.FormGroup>
              <S.FormGroup>
                <S.Label>Dimensions</S.Label>
                <S.Input value={dimensions} readOnly />
              </S.FormGroup>
            </S.FormRow>
          ) : null}
        </>
      )}
    </S.FormSection>
  );
};
